import { useNavigate, useParams } from 'react-router-dom'
import { Box } from '@mui/material'
import {
  useBaseSourceSiteWorkCentre,
  useGenerateTemplates,
  useUpdateTemplateGeneration,
} from 'Features/Projects/Scenarios/Templates/PlanTemplateSummary.data'
import { Page } from 'Common/Page/Page'
import { useWindowSize } from 'Common/hooks/useWindowSize.hook'
import { Button, PageHeader } from 'Common/Supplyvue-ui'
import { Loader } from 'Common/Loader/Loader'
import { PlanTemplateSummaryTable } from 'Features/Projects/Scenarios/Templates/PlanTemplateSummary.Table'
import { GridRowId } from '@mui/x-data-grid-premium'
import {
  BaseSourceSiteWorkCentre,
  Status,
  STATUS_MAP,
} from 'Features/Projects/Scenarios/Templates/PlanTemplateSummary.types'
import { MutableRefObject, useEffect, useState } from 'react'
import { toast } from 'react-hot-toast'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'

export const PlanTemplateSummaryView = () => {
  const navigate = useNavigate()
  const { view_width, view_height } = useWindowSize()
  const { projectId, scenarioId } = useParams<{
    projectId: string
    scenarioId: string
  }>() as { projectId: string; scenarioId: string }

  const { data, isLoading, refetch, isRefetching } =
    useBaseSourceSiteWorkCentre({
      projectId,
      scenarioId,
    })

  const [dataState, setDataState] = useState<BaseSourceSiteWorkCentre[]>([])

  useEffect(() => {
    if (data) {
      setDataState(data)
    }
  }, [data])

  const onSuccess = () => {
    toast.success('Update Successful')
  }

  const onError = () => {
    toast.error('Update Failed')
  }

  const { mutate: update } = useUpdateTemplateGeneration(onSuccess, onError)

  const handleSave = (
    unsavedChanges: MutableRefObject<{
      unsavedRows: Record<GridRowId, BaseSourceSiteWorkCentre>
      rowsBeforeChange: Record<GridRowId, BaseSourceSiteWorkCentre>
    }>
  ) => {
    const { unsavedRows } = unsavedChanges.current

    const newState: BaseSourceSiteWorkCentre[] = dataState.map(
      (setting: BaseSourceSiteWorkCentre) => unsavedRows[setting.id] ?? setting
    )

    update({
      projectId: projectId,
      scenarioId: scenarioId,
      state: newState,
    })
  }

  const { mutate: generate, isLoading: isTemplateGenerating } =
    useGenerateTemplates(
      () => {
        toast.success('Templates Generated Successfully')
      },
      () => {
        toast.error('Templates Generation Failed')
      }
    )

  const handleRunTemplates = (selectedRows: GridRowId[]) => {
    const modeInputs = dataState
      .filter((setting) => selectedRows.includes(setting.id))
      .map((setting: BaseSourceSiteWorkCentre) => ({
        workcentreId: setting.workCentreKey,
        templateMethod: setting.preferredTemplate,
      }))

    // UPDATE THE dataState to set the selected rows spinner going
    // @ts-ignore
    const newState: BaseSourceSiteWorkCentre = dataState.map(
      (setting: BaseSourceSiteWorkCentre) => {
        if (selectedRows.includes(setting.id)) {
          const statusKey =
            STATUS_MAP[setting.preferredTemplate] ?? 'constrainedStatus'
          return {
            ...setting,
            [statusKey]: 'initiated' as Status,
          }
        }
        return setting
      }
    )

    if (newState) {
      // @ts-ignore
      setDataState(newState)
      update({
        projectId: projectId,
        scenarioId: scenarioId,
        // @ts-ignore
        state: newState,
      })
    }

    if (modeInputs.length > 0) {
      generate({
        projectId: projectId,
        scenarioId: scenarioId,
        modeInputs: modeInputs,
      })
    }
  }

  const handleRefresh = () => {
    refetch()
  }

  const Return = () => {
    return (
      <Box display="flex">
        <Button
          variant="text"
          sx={{ fontWeight: 600 }}
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/projects/${projectId}/scenarios`)}
        >
          Back to Scenarios
        </Button>
      </Box>
    )
  }

  return (
    <Page>
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          justifyContent: 'flex-start',
          maxWidth: view_width,
        }}
      >
        <PageHeader title={'Template Summary'} rightElement={<Return />} />
        {isLoading && <Loader />}
        {data && (
          <PlanTemplateSummaryTable
            data={dataState}
            handleSave={handleSave}
            handleRunTemplates={handleRunTemplates}
            isTemplateGenerating={isTemplateGenerating}
            handleRefresh={handleRefresh}
            isRefetching={isRefetching}
          />
        )}
      </Box>
    </Page>
  )
}
